<template>
  <section class="ead-page">
    <titlebar :title-stack="[tituloPagina]" />

    <ValidationObserver v-slot="{ handleSubmit }">
      <form @submit.prevent="handleSubmit(salvar)">
        <div class="box cadastro-header">
          <div class="cadastro-header__identificacao">
            <span class="cadastro-header__icone">
              <b-icon icon="school" />
            </span>
            <div>
              <p class="cadastro-header__legenda">Catálogo de treinamentos</p>
              <h1 class="title is-4 mb-1">{{ tituloPagina }}</h1>
              <p class="has-text-grey">
                Organize as informações que ajudam o funcionário a identificar
                o curso antes de começar.
              </p>
            </div>
          </div>
          <b-tag
            v-if="model.id"
            rounded
            :type="model.ativo ? 'is-success' : 'is-light'"
          >
            {{ model.ativo ? 'Curso ativo' : 'Curso inativo' }}
          </b-tag>
        </div>

        <div class="columns cadastro-grid">
          <div class="column is-8-desktop">
            <div class="box formulario-card">
              <div class="secao-titulo">
                <b-icon icon="text-box-outline" type="is-primary" />
                <div>
                  <h2 class="title is-5 mb-1">Informações principais</h2>
                  <p class="has-text-grey is-size-7">
                    Dados usados na listagem e na apresentação do curso.
                  </p>
                </div>
              </div>

              <div class="columns">
                <div class="column is-4">
                  <ValidationProvider
                    rules="required"
                    name="Código"
                    v-slot="{ errors }"
                  >
                    <b-field
                      label="Código"
                      :message="errors"
                      :type="{ 'is-danger': errors[0] }"
                    >
                      <b-input
                        v-model="model.codigo"
                        maxlength="40"
                        icon="barcode"
                        placeholder="Ex.: INT-001"
                      />
                    </b-field>
                  </ValidationProvider>
                </div>
                <div class="column">
                  <ValidationProvider
                    rules="required"
                    name="Nome"
                    v-slot="{ errors }"
                  >
                    <b-field
                      label="Nome do curso"
                      :message="errors"
                      :type="{ 'is-danger': errors[0] }"
                    >
                      <b-input
                        v-model="model.nome"
                        maxlength="160"
                        icon="format-title"
                        placeholder="Informe um nome claro e objetivo"
                      />
                    </b-field>
                  </ValidationProvider>
                </div>
              </div>

              <b-field label="Descrição">
                <b-input
                  v-model="model.descricao"
                  type="textarea"
                  maxlength="4000"
                  rows="5"
                  placeholder="Explique o objetivo e o que será aprendido neste curso"
                />
              </b-field>

              <div class="columns">
                <div class="column">
                  <b-field
                    label="Imagem de capa (URL)"
                    message="Use uma imagem horizontal para melhor visualização."
                  >
                    <b-input
                      v-model="model.imagem"
                      maxlength="1000"
                      icon="image-outline"
                      placeholder="https://..."
                    />
                  </b-field>
                </div>
                <div class="column is-4">
                  <b-field label="Carga horária">
                    <b-numberinput
                      v-model="model.cargaHorariaMinutos"
                      min="1"
                      controls-position="compact"
                    />
                    <p class="control unidade-campo">minutos</p>
                  </b-field>
                </div>
              </div>
            </div>

            <div class="box formulario-card">
              <div class="secao-titulo">
                <b-icon icon="tune-variant" type="is-primary" />
                <div>
                  <h2 class="title is-5 mb-1">Configurações do curso</h2>
                  <p class="has-text-grey is-size-7">
                    Defina disponibilidade, obrigatoriedade e avaliação.
                  </p>
                </div>
              </div>

              <div class="opcao-card">
                <div>
                  <p class="has-text-weight-semibold">Curso ativo</p>
                  <p class="has-text-grey is-size-7">
                    Cursos ativos podem ser liberados para os funcionários.
                  </p>
                </div>
                <b-switch v-model="model.ativo" type="is-success" />
              </div>

              <div class="opcao-card">
                <div>
                  <p class="has-text-weight-semibold">Curso obrigatório</p>
                  <p class="has-text-grey is-size-7">
                    Identifica este treinamento como obrigatório no catálogo.
                  </p>
                </div>
                <b-switch v-model="model.obrigatorio" />
              </div>

              <div class="opcao-card opcao-card--avaliacao">
                <div class="opcao-card__conteudo">
                  <div>
                    <p class="has-text-weight-semibold">Avaliação final</p>
                    <p class="has-text-grey is-size-7">
                      Exige aprovação após a conclusão de todas as aulas.
                    </p>
                  </div>
                  <b-switch v-model="model.possuiAvaliacaoFinal" />
                </div>
                <p v-if="model.possuiAvaliacaoFinal" class="has-text-grey is-size-7 mt-3">
                  As perguntas podem ser configuradas depois de salvar, na tela de conteúdo.
                </p>
                <div v-if="model.possuiAvaliacaoFinal" class="nota-minima">
                  <b-field label="Nota mínima para aprovação">
                    <b-numberinput
                      v-model="model.notaMinima"
                      min="0"
                      max="100"
                      controls-position="compact"
                    />
                    <p class="control unidade-campo">%</p>
                  </b-field>
                </div>
              </div>
            </div>
          </div>

          <div class="column is-4-desktop">
            <aside class="box preview-card">
              <p class="preview-card__legenda">Pré-visualização</p>
              <div class="preview-capa">
                <img
                  v-if="model.imagem && !imagemInvalida"
                  :src="model.imagem"
                  alt="Capa do curso"
                  @error="imagemInvalida = true"
                />
                <div v-else class="preview-capa__vazia">
                  <b-icon icon="image-outline" size="is-large" />
                  <span>Adicione a URL da imagem de capa</span>
                </div>
              </div>
              <div class="preview-conteudo">
                <b-tag v-if="model.codigo" rounded>{{ model.codigo }}</b-tag>
                <h3 class="title is-5 mt-3 mb-2">
                  {{ model.nome || 'Nome do curso' }}
                </h3>
                <p class="preview-descricao">
                  {{
                    model.descricao ||
                    'A descrição do curso será exibida neste espaço.'
                  }}
                </p>
                <div class="preview-meta">
                  <span>
                    <b-icon icon="clock-outline" size="is-small" />
                    {{ duracaoCurso }}
                  </span>
                  <span v-if="model.possuiAvaliacaoFinal">
                    <b-icon icon="clipboard-check-outline" size="is-small" />
                    Com avaliação
                  </span>
                </div>
              </div>
            </aside>
          </div>
        </div>

        <b-message v-if="erroSalvar" type="is-danger" has-icon>
          {{ erroSalvar }}
        </b-message>

        <div class="box formulario-acoes">
          <b-button icon-left="arrow-left" @click="$router.back()">
            Cancelar
          </b-button>
          <b-button
            native-type="submit"
            type="is-primary"
            icon-left="content-save"
            :loading="salvando"
          >
            Salvar e continuar
          </b-button>
        </div>
      </form>
    </ValidationObserver>

    <b-loading :active.sync="carregando" :is-full-page="false" />
  </section>
</template>

<script>
import http from '@/http'
import titlebar from '@/components/titlebar.vue'
import { mapState } from 'vuex'

export default {
  name: 'EadCursoCadastro',
  components: { titlebar },
  data: () => ({
    model: {
      id: 0,
      codigo: '',
      nome: '',
      descricao: '',
      imagem: '',
      cargaHorariaMinutos: 60,
      notaMinima: null,
      possuiAvaliacaoFinal: false,
      obrigatorio: false,
      ativo: false,
    },
    carregando: false,
    salvando: false,
    erroSalvar: '',
    imagemInvalida: false,
  }),
  computed: {
    ...mapState(['unidadeId']),
    tituloPagina() {
      return this.model.id ? 'Editar curso' : 'Novo curso'
    },
    duracaoCurso() {
      const minutos = Number(this.model.cargaHorariaMinutos) || 0
      if (minutos < 60) return `${minutos} min`
      const horas = Math.floor(minutos / 60)
      const restante = minutos % 60
      return restante ? `${horas}h ${restante}min` : `${horas}h`
    },
  },
  watch: {
    'model.imagem'() {
      this.imagemInvalida = false
    },
  },
  mounted() {
    if (!this.$route.params.id) return

    this.carregando = true
    http
      .get('/api/manutencao/ead-curso', {
        params: { id: this.$route.params.id },
      })
      .then((r) => {
        this.model = { ...this.model, ...r.data }
      })
      .finally(() => (this.carregando = false))
  },
  methods: {
    salvar() {
      this.erroSalvar = ''
      this.salvando = true
      if (!this.model.id) this.model.unidadeId = this.unidadeId
      http
        .post('/api/manutencao/ead-curso', this.model)
        .then((r) => {
          this.$buefy.toast.open({
            message: 'Curso salvo.',
            type: 'is-success',
          })
          this.$router.push({
            name: 'ead-curso-conteudo',
            params: { id: r.data.id },
          })
        })
        .catch((erro) => {
          this.erroSalvar =
            (erro && erro.data && erro.data.erro) ||
            'Não foi possível salvar o curso. Tente novamente.'
        })
        .finally(() => (this.salvando = false))
    },
  },
}
</script>

<style scoped>
.ead-page {
  min-height: 100%;
  position: relative;
}

.cadastro-header,
.cadastro-header__identificacao,
.secao-titulo,
.opcao-card,
.opcao-card__conteudo,
.preview-meta,
.formulario-acoes {
  align-items: center;
  display: flex;
}

.cadastro-header {
  border-left: 4px solid #00b89c;
  justify-content: space-between;
  margin-bottom: 1.25rem;
}

.cadastro-header__identificacao,
.secao-titulo {
  gap: 0.85rem;
}

.cadastro-header__icone {
  align-items: center;
  background: rgba(0, 184, 156, 0.12);
  border-radius: 50%;
  color: #008f79;
  display: inline-flex;
  flex: 0 0 auto;
  height: 3rem;
  justify-content: center;
  width: 3rem;
}

.cadastro-header__legenda,
.preview-card__legenda {
  color: #7a7a7a;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.cadastro-grid {
  align-items: flex-start;
}

.formulario-card,
.preview-card {
  border: 1px solid #ededed;
  box-shadow: 0 2px 8px rgba(10, 10, 10, 0.05);
}

.secao-titulo {
  border-bottom: 1px solid #ededed;
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
}

.unidade-campo {
  align-items: center;
  background: #f5f5f5;
  border: 1px solid #dbdbdb;
  border-left: 0;
  color: #7a7a7a;
  display: flex;
  padding: 0 0.75rem;
}

.opcao-card {
  border: 1px solid #ededed;
  border-radius: 7px;
  gap: 1rem;
  justify-content: space-between;
  padding: 1rem;
}

.opcao-card + .opcao-card {
  margin-top: 0.75rem;
}

.opcao-card--avaliacao {
  align-items: stretch;
  display: block;
}

.opcao-card__conteudo {
  justify-content: space-between;
}

.nota-minima {
  border-top: 1px solid #ededed;
  margin-top: 1rem;
  padding-top: 1rem;
  width: min(100%, 320px);
}

.preview-card {
  overflow: hidden;
  padding: 0;
  position: sticky;
  top: 1rem;
}

.preview-card__legenda {
  padding: 1rem 1rem 0.75rem;
}

.preview-capa {
  aspect-ratio: 16 / 9;
  background: #f5f5f5;
  overflow: hidden;
}

.preview-capa img {
  height: 100%;
  object-fit: cover;
  width: 100%;
}

.preview-capa__vazia {
  align-items: center;
  color: #a0a0a0;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  height: 100%;
  justify-content: center;
  padding: 1rem;
  text-align: center;
}

.preview-conteudo {
  padding: 1.25rem;
}

.preview-descricao {
  color: #7a7a7a;
  display: -webkit-box;
  font-size: 0.9rem;
  line-height: 1.5;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
}

.preview-meta {
  border-top: 1px solid #ededed;
  color: #7a7a7a;
  flex-wrap: wrap;
  font-size: 0.8rem;
  gap: 1rem;
  margin-top: 1rem;
  padding-top: 1rem;
}

.preview-meta span {
  align-items: center;
  display: inline-flex;
  gap: 0.3rem;
}

.formulario-acoes {
  gap: 0.75rem;
  justify-content: flex-end;
}

@media screen and (max-width: 768px) {
  .cadastro-header,
  .cadastro-header__identificacao,
  .opcao-card__conteudo {
    align-items: flex-start;
  }

  .cadastro-header {
    gap: 1rem;
  }

  .cadastro-header__identificacao {
    flex-direction: column;
  }

  .preview-card {
    position: static;
  }

  .formulario-acoes {
    flex-direction: column-reverse;
  }

  .formulario-acoes .button {
    width: 100%;
  }
}
</style>
